// src/pages/Signuppage.jsx
import React, { useState, useEffect, memo } from "react";
import toast from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import SuccessPopup from "../components/common/SuccessPopup";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { axiosInstance } from "../lib/axiosInstance";
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

// Fix for default Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Memoized component for displaying the Mock Aadhaar Card
const MemoizedMockAadhaarCard = memo(({ cardData, aadhaarNumber }) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const placeholderSrc = "/assets/placeholder-avatar.png";

  useEffect(() => {
    setImageLoadFailed(false);
  }, [cardData?.photo_link]);

  if (!cardData) return null;

  // Handle base64 image data
  const aadhaarPhotoSrc = cardData.photo_link
    ? `data:image/jpeg;base64,${cardData.photo_link}`
    : placeholderSrc;

  const currentPhotoToDisplay = imageLoadFailed
    ? placeholderSrc
    : aadhaarPhotoSrc;

  const handleImageError = () => {
    if (!imageLoadFailed && currentPhotoToDisplay === aadhaarPhotoSrc) {
      setImageLoadFailed(true);
    }
  };

  const genderText = (genderCode) => {
    if (genderCode === "M") return "पुरुष / MALE";
    if (genderCode === "F") return "महिला / FEMALE";
    if (genderCode === "T") return "TRANSGENDER"; // Common representation
    return genderCode || "N/A";
  };

  return (
    <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg text-xs font-sans max-w-[300px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 border-b border-gray-300 pb-1.5">
        <img
          src="https://upload.wikimedia.org/wikipedia/en/thumb/c/cf/Aadhaar_Logo.svg/1200px-Aadhaar_Logo.svg.png"
          alt="Aadhaar Logo"
          className="h-7"
        />
        <div className="text-right">
          <p className="text-[9px] font-bold text-gray-700">भारत सरकार</p>
          <p className="text-[8px] text-gray-500">Government of India</p>
        </div>
      </div>

      {/* Tricolor Strip */}
      <div className="flex h-1.5 mb-2.5 rounded-sm overflow-hidden">
        <div className="w-1/3 bg-orange-500"></div>
        <div className="w-1/3 bg-white border-x border-gray-200"></div>
        <div className="w-1/3 bg-green-600"></div>
      </div>

      {/* Content */}
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0">
          <img
            key={currentPhotoToDisplay}
            src={currentPhotoToDisplay}
            alt="Aadhaar Pic"
            className="w-16 h-20 rounded-md object-cover border-2 border-gray-200 shadow-sm"
            onError={handleImageError}
          />
        </div>
        <div className="flex-grow text-left space-y-1 text-[10px]">
          <p className="font-bold text-sm text-gray-800 leading-tight">
            {cardData.name}
          </p>
          <p className="text-gray-600">
            जन्म तिथि/DOB:{" "}
            <span className="font-semibold text-gray-700">{cardData.dob}</span>
          </p>
          <p className="text-gray-600">
            लिंग/Gender:{" "}
            <span className="font-semibold text-gray-700">
              {genderText(cardData.gender)}
            </span>
          </p>
        </div>
      </div>

      <div className="text-center mt-2.5 mb-1.5">
        <p className="text-xl font-mono tracking-wider text-gray-800 font-bold">
          XXXX XXXX {aadhaarNumber ? aadhaarNumber.slice(-4) : "XXXX"}
        </p>
      </div>

      <div className="mt-2 pt-1.5 border-t border-dashed border-gray-300 text-left">
        <p className="text-[10px] text-gray-500 font-medium">पता/Address:</p>
        <p className="text-[9px] text-gray-700 leading-snug">
          {cardData.address || "N/A"}
        </p>
      </div>

      <div className="mt-3 text-center">
        <span className="inline-flex items-center bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 mr-1.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          AADHAAR VERIFIED
        </span>
      </div>
    </div>
  );
});

// Memoized component for displaying the Mock GST Card
const MemoizedMockGstCard = memo(({ cardData }) => {
  if (!cardData) return null;

  return (
    <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg text-xs font-sans max-w-[300px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5 border-b border-gray-300 pb-1.5">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/55/Emblem_of_India.svg/1200px-Emblem_of_India.svg.png"
          alt="Emblem of India"
          className="h-8"
        />
        <div className="text-right">
          <p className="text-[10px] font-bold text-gray-700">
            वस्तु एवं सेवा कर
          </p>
          <p className="text-[9px] font-bold text-gray-700">
            Goods and Services Tax
          </p>
          <p className="text-[7px] text-gray-500">Government of India</p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-1.5 text-left text-[10px]">
        <p className="text-gray-600">
          GSTIN:{" "}
          <span className="font-mono font-bold text-gray-800 text-sm">
            {cardData.GSTIN}
          </span>
        </p>
        <p className="text-gray-600">
          Legal Name:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.legal_name_of_business}
          </span>
        </p>
        <p className="text-gray-600">
          Trade Name:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.trade_name_of_business || "N/A"}
          </span>
        </p>
        <p className="text-gray-600">
          Date of Registration:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.date_of_registration}
          </span>
        </p>
        <p className="text-gray-600">
          Status:{" "}
          <span
            className={`font-bold py-0.5 px-1.5 rounded-sm text-[9px] ${
              cardData.gst_in_status === "Active"
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-600"
            }`}
          >
            {cardData.gst_in_status}
          </span>
        </p>
        <div className="pt-1.5 border-t border-dashed border-gray-300 mt-1.5">
          <p className="text-[10px] text-gray-500 font-medium">
            Principal Place of Business:
          </p>
          <p className="text-[9px] text-gray-700 leading-snug">
            {cardData.principal_place_address || "N/A"}
          </p>
        </div>
      </div>
      <div className="mt-3 text-center">
        <span className="inline-flex items-center bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 mr-1.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          GSTIN VERIFIED
        </span>
      </div>
    </div>
  );
});

// Draggable Marker Component for Leaflet Map
const DraggableMarker = ({ position, onPositionChange }) => {
  const [currentPosition, setCurrentPosition] = useState(position);
  const map = useMap();

  useEffect(() => {
    setCurrentPosition(position); // Update marker when prop changes
    if (position && position.lat && position.lng) {
      map.setView(
        [position.lat, position.lng],
        map.getZoom() < 13 ? 13 : map.getZoom()
      );
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setCurrentPosition(e.latlng);
      if (onPositionChange) onPositionChange(e.latlng);
    },
  });

  return currentPosition && currentPosition.lat && currentPosition.lng ? (
    <Marker
      position={currentPosition}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const newLatLng = e.target.getLatLng();
          setCurrentPosition(newLatLng);
          if (onPositionChange) onPositionChange(newLatLng);
        },
      }}
    />
  ) : null;
};

const SignupPage = () => {
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    companyName: "",
    establishmentYear: "",
    engineerName: "",
    gender: "",
    email: "",
    password: "",
    gst: "",
    aadhaarNumber: "",
    contactNumber: "",
    whatsappNumber: "",
    addressLine1: "",
    addressLine2: "",
    village: "",
    district: "",
    taluk: "",
    postcode: "",
    latitude: "", // Store as string
    longitude: "", // Store as string
    qualification: "",
    governmentLicense: null,
    graduationCertificate: null,
    profileImage: null,
    agreementConfirmed: false,
    privacyPolicyConfirmed: false,
    warrantyConfirmed: false,
    aadhaarOtp: "",
    aadhaarRefId: "",
    aadhaarVerified: false,
    gstVerified: false,
    verifiedAadhaarData: null,
    verifiedGstinData: null,
  });

  const { signupBuilder, isSigningUp } = useAuthStore();
  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const [isVerifyingAadhaar, setIsVerifyingAadhaar] = useState(false);
  const [isVerifyingAadhaarOtp, setIsVerifyingAadhaarOtp] = useState(false);
  const [isVerifyingGst, setIsVerifyingGst] = useState(false);
  const [showAadhaarOtpInput, setShowAadhaarOtpInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [aadhaarCardData, setAadhaarCardData] = useState(null);
  const [gstCardData, setGstCardData] = useState(null);

  const handleMapInteraction = (latlng) => {
    setFormData((prev) => ({
      ...prev,
      latitude: latlng.lat.toFixed(6).toString(),
      longitude: latlng.lng.toFixed(6).toString(),
    }));
    // toast.info("Location updated from map."); // Optional: less frequent toasts
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prev) => ({
            ...prev,
            latitude: latitude.toFixed(6).toString(),
            longitude: longitude.toFixed(6).toString(),
          }));
          toast.success("Location detected successfully!");
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error(
            "Unable to get your location. Please click on the map or enter manually."
          );
        }
      );
    } else {
      toast.error(
        "Geolocation is not supported. Please click on the map or enter manually."
      );
    }
  };

  const verifyAadhaar = async () => {
    if (
      !formData.aadhaarNumber ||
      formData.aadhaarNumber.replace(/\s/g, "").length !== 12
    ) {
      toast.error("Please enter a valid 12-digit Aadhaar number.");
      return;
    }
    setIsVerifyingAadhaar(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/aadhaar/generate-otp",
        {
          aadhaarNumber: formData.aadhaarNumber.replace(/\s/g, ""),
        }
      );
      if (response.data.ref_id) {
        setFormData({ ...formData, aadhaarRefId: response.data.ref_id });
        setShowAadhaarOtpInput(true);
        toast.success(response.data.message || "OTP sent successfully.");
      } else {
        toast.error(response.data.message || "Failed to send Aadhaar OTP.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Aadhaar verification request failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingAadhaar(false);
    }
  };

  const verifyAadhaarOTP = async () => {
    if (!formData.aadhaarOtp || formData.aadhaarOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }
    setIsVerifyingAadhaarOtp(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/aadhaar/verify-otp",
        {
          otp: formData.aadhaarOtp,
          ref_id: formData.aadhaarRefId,
          aadhaarNumber: formData.aadhaarNumber.replace(/\s/g, ""),
        }
      );
      if (
        response.data.message === "Aadhaar verified successfully." &&
        response.data.details
      ) {
        const details = response.data.details;
        setFormData((prev) => ({
          ...prev,
          aadhaarVerified: true,
          engineerName: details.name || prev.engineerName,
          gender:
            details.gender === "M"
              ? "Male"
              : details.gender === "F"
              ? "Female"
              : details.gender === "T" // Adding Transgender
              ? "Transgender"
              : prev.gender,
          verifiedAadhaarData: details, // Store all details for the card
        }));
        setAadhaarCardData(details);
        setShowAadhaarOtpInput(false);
        toast.success("Aadhaar verified successfully!");
      } else {
        toast.error(
          response.data.message || "Invalid OTP or verification failed."
        );
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Aadhaar OTP verification failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingAadhaarOtp(false);
    }
  };

  const verifyGST = async () => {
    if (!formData.gst || formData.gst.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN.");
      return;
    }
    setIsVerifyingGst(true);
    try {
      const response = await axiosInstance.post("/builders/auth/gstin/verify", {
        gstin: formData.gst,
      });
      if (
        response.data.message === "GSTIN verified successfully." &&
        response.data.details
      ) {
        const details = response.data.details;
        setFormData((prev) => ({
          ...prev,
          gstVerified: true,
          companyName: details.legal_name_of_business || prev.companyName,
          verifiedGstinData: details, // Store all details for the card
        }));
        setGstCardData(details);
        toast.success("GSTIN verified successfully!");
      } else {
        toast.error(response.data.message || "GSTIN verification failed.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "GSTIN verification request failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingGst(false);
    }
  };

  const steps = [
    {
      title: "Company Details",
      fields: [
        {
          label: "GSTIN",
          type: "text",
          id: "gst",
          maxLength: 15,
          required: true,
          verifyButton: true,
          verifyFunction: verifyGST,
          isLoading: isVerifyingGst,
          isVerified: formData.gstVerified,
          disabled: formData.gstVerified,
        },
        {
          label: "Company Name",
          type: "text",
          id: "companyName",
          required: true,
          disabled: formData.gstVerified,
        },
        {
          label: "Establishment Year",
          type: "number",
          id: "establishmentYear",
          required: true,
          min: 1900,
          max: new Date().getFullYear(),
        },
        {
          label: "Aadhaar Number",
          type: "text",
          id: "aadhaarNumber",
          maxLength: 14, // Allows for spaces "xxxx xxxx xxxx"
          required: true,
          verifyButton: true,
          verifyFunction: verifyAadhaar,
          isLoading: isVerifyingAadhaar,
          isVerified: formData.aadhaarVerified,
          disabled: formData.aadhaarVerified,
        },
        {
          label: "Aadhaar OTP",
          type: "text",
          id: "aadhaarOtp",
          maxLength: 6,
          required: true,
          verifyButton: true,
          verifyFunction: verifyAadhaarOTP,
          isLoading: isVerifyingAadhaarOtp,
          showWhenVerifying: showAadhaarOtpInput,
        },
        {
          label: "Engineer Name",
          type: "text",
          id: "engineerName",
          required: true,
          disabled: formData.aadhaarVerified,
        },
        {
          label: "Gender",
          type: "select",
          id: "gender",
          options: ["", "Male", "Female", "Transgender", "Other"], // Added Transgender
          required: true,
          disabled: formData.aadhaarVerified,
        },
        { label: "Email", type: "email", id: "email", required: true },
        {
          label: "Create Password",
          type: "password",
          id: "password",
          required: true,
          minLength: 8,
        },
        {
          label: "Contact Number",
          type: "tel",
          id: "contactNumber",
          required: true,
          maxLength: 10,
        },
        {
          label: "WhatsApp Number",
          type: "tel",
          id: "whatsappNumber",
          maxLength: 10,
        },
      ],
    },
    {
      title: "Address",
      fields: [
        {
          label: "Address Line 1",
          type: "text",
          id: "addressLine1",
          required: true,
        },
        { label: "Address Line 2", type: "text", id: "addressLine2" },
        { label: "Village/Town", type: "text", id: "village", required: true },
        { label: "District", type: "text", id: "district", required: true },
        { label: "Taluk", type: "text", id: "taluk", required: true },
        {
          label: "Postcode",
          type: "text",
          id: "postcode",
          required: true,
          maxLength: 6,
        },
        {
          label: "Latitude",
          type: "text", // Keep as text for manual input flexibility
          id: "latitude",
          required: true,
        },
        {
          label: "Longitude",
          type: "text", // Keep as text
          id: "longitude",
          required: true,
        },
      ],
    },
    {
      title: "Documentation",
      fields: [
        {
          label: "Qualification",
          type: "text",
          id: "qualification",
          required: true,
        },
        {
          label: "Government License (PDF, JPG, PNG - Max 5MB)",
          type: "file",
          id: "governmentLicense",
          required: true,
          accept: ".pdf,.jpg,.jpeg,.png",
        },
        {
          label: "Graduation Certificate (PDF, JPG, PNG - Max 5MB)",
          type: "file",
          id: "graduationCertificate",
          required: true,
          accept: ".pdf,.jpg,.jpeg,.png",
        },
      ],
    },
    {
      title: "Profile & Agreements",
      fields: [],
    },
  ];

  const handleInputChange = (e, id) => {
    const { value, type } = e.target;
    let processedValue = value;

    if (
      type === "number" ||
      [
        "establishmentYear",
        "postcode",
        "contactNumber",
        "whatsappNumber",
        "aadhaarOtp", // Aadhaar number formatting handled separately
      ].includes(id)
    ) {
      processedValue = value.replace(/\D/g, "");
    }
    // Specific formatting for Aadhaar Number
    if (id === "aadhaarNumber") {
      let rawValue = value.replace(/\D/g, "").substring(0, 12); // Remove non-digits, limit to 12
      let formattedValue = "";
      for (let i = 0; i < rawValue.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formattedValue += " "; // Add space every 4 digits
        }
        formattedValue += rawValue[i];
      }
      // Update the input field's value directly for visual feedback
      e.target.value = formattedValue;
      // Store the raw, unformatted digits in formData
      processedValue = rawValue;
    } else if (id === "gst") {
      processedValue = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .substring(0, 15);
      e.target.value = processedValue;
    }

    // Length restrictions
    if (id === "establishmentYear" && processedValue.length > 4)
      processedValue = processedValue.slice(0, 4);
    if (id === "postcode" && processedValue.length > 6)
      processedValue = processedValue.slice(0, 6);
    if (
      (id === "contactNumber" || id === "whatsappNumber") &&
      processedValue.length > 10
    )
      processedValue = processedValue.slice(0, 10);
    if (id === "aadhaarOtp" && processedValue.length > 6)
      processedValue = processedValue.slice(0, 6);

    // For latitude and longitude, allow negative and decimal
    if (id === "latitude" || id === "longitude") {
      processedValue = value.replace(/[^\d.-]/g, ""); // Allow digits, dot, and hyphen
    }

    setFormData((prev) => ({ ...prev, [id]: processedValue }));
  };

  const handleFileChange = (e, id) => {
    const file = e.target.files[0];
    if (file) {
      const fieldLabel =
        steps[currentStep].fields.find((f) => f.id === id)?.label || "File";
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type for ${fieldLabel}. PDF, JPG, PNG only.`);
        e.target.value = null;
        return;
      }
      if (file.size > maxSize) {
        toast.error(`File size for ${fieldLabel} exceeds 5MB.`);
        e.target.value = null;
        return;
      }
      setFormData({ ...formData, [id]: file });
    }
  };

  const handleprofileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid profile image type. JPG, PNG, WEBP only.");
        e.target.value = null;
        return;
      }
      if (file.size > maxSize) {
        toast.error("Profile image size exceeds 2MB.");
        e.target.value = null;
        return;
      }
      setProfileImagePreview(URL.createObjectURL(file));
      setFormData({ ...formData, profileImage: file });
    }
  };

  const handleCheckboxChange = (id) => {
    setFormData({ ...formData, [id]: !formData[id] });
  };

  const validateStep = () => {
    const stepFields = steps[currentStep].fields;
    for (let field of stepFields) {
      if (
        field.required &&
        (!formData[field.id] ||
          (typeof formData[field.id] === "string" &&
            formData[field.id].trim() === ""))
      ) {
        if (field.id === "aadhaarOtp" && !showAadhaarOtpInput) continue;
        toast.error(`Please fill out the '${field.label}' field.`);
        return false;
      }
      // Specific validations
      if (
        field.id === "email" &&
        formData.email &&
        !/\S+@\S+\.\S+/.test(formData.email)
      ) {
        toast.error("Please enter a valid email address.");
        return false;
      }
      if (field.id === "password" && formData.password) {
        const pass = formData.password;
        if (pass.length < 8) {
          toast.error("Password: min 8 chars.");
          return false;
        }
        if ((pass.match(/\d/g) || []).length < 2) {
          toast.error("Password: at least 2 numbers.");
          return false;
        }
        if (!/[a-zA-Z]/.test(pass)) {
          toast.error("Password: at least one letter.");
          return false;
        }
      }
      if (
        field.id === "establishmentYear" &&
        formData.establishmentYear &&
        (parseInt(formData.establishmentYear) < 1900 ||
          parseInt(formData.establishmentYear) > new Date().getFullYear())
      ) {
        toast.error("Please enter a valid establishment year.");
        return false;
      }
      if (
        (field.id === "contactNumber" || field.id === "whatsappNumber") &&
        formData[field.id] &&
        formData[field.id].length !== 10
      ) {
        toast.error(`Please enter a valid 10-digit ${field.label}.`);
        return false;
      }
      if (
        field.id === "aadhaarNumber" &&
        formData.aadhaarNumber &&
        formData.aadhaarNumber.replace(/\s/g, "").length !== 12
      ) {
        toast.error("Please enter a valid 12-digit Aadhaar Number.");
        return false;
      }
      if (
        field.id === "aadhaarNumber" &&
        field.required &&
        !formData.aadhaarVerified
      ) {
        toast.error("Please verify your Aadhaar number.");
        return false;
      }
      if (field.id === "gst" && field.required && !formData.gstVerified) {
        toast.error("Please verify your GSTIN.");
        return false;
      }
      if (
        field.id === "aadhaarOtp" &&
        showAadhaarOtpInput &&
        formData.aadhaarOtp &&
        formData.aadhaarOtp.length !== 6
      ) {
        toast.error("Please enter the 6-digit Aadhaar OTP.");
        return false;
      }
      if (field.id === "gst" && formData.gst && formData.gst.length !== 15) {
        toast.error("Please enter a valid 15-character GSTIN.");
        return false;
      }
      if (
        field.id === "postcode" &&
        formData.postcode &&
        formData.postcode.length !== 6
      ) {
        toast.error("Please enter a valid 6-digit Postcode.");
        return false;
      }
    }
    if (currentStep === steps.length - 1) {
      if (!formData.agreementConfirmed) {
        toast.error("Please agree to Terms and Conditions.");
        return false;
      }
      if (!formData.privacyPolicyConfirmed) {
        toast.error("Please agree to the Privacy Policy.");
        return false;
      }
      if (!formData.warrantyConfirmed) {
        toast.error("Please agree to provide warranty service.");
        return false;
      }
      if (!formData.profileImage) {
        toast.error("Please upload a profile image.");
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (!validateStep()) return;
    if (currentStep < steps.length - 1) setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    try {
      const result = await signupBuilder(formData);
      if (result.success) {
        setPopupMessage(result.message);
        setIsPopupOpen(true);
      }
    } catch (error) {
      console.error("Signup failed:", error);
    }
  };

  const closePopupAndRedirect = () => {
    setIsPopupOpen(false);
    navigate("/login?type=builder");
  };

  const isAddressStep = currentStep === 1;
  const mapCenter =
    formData.latitude &&
    formData.longitude &&
    !isNaN(parseFloat(formData.latitude)) &&
    !isNaN(parseFloat(formData.longitude))
      ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
      : [20.5937, 78.9629]; // Default to India

  const markerPos =
    formData.latitude &&
    formData.longitude &&
    !isNaN(parseFloat(formData.latitude)) &&
    !isNaN(parseFloat(formData.longitude))
      ? {
          lat: parseFloat(formData.latitude),
          lng: parseFloat(formData.longitude),
        }
      : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100">
      <SuccessPopup
        isOpen={isPopupOpen}
        onClose={closePopupAndRedirect}
        message={popupMessage}
      />
      <div className="w-full max-w-4xl p-6 border bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-semibold text-center text-gray-700 mb-8">
          Builder Registration
        </h2>
        <div className="mb-6">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center flex-1 relative"
              >
                {index > 0 && (
                  <div
                    className="absolute w-full h-0.5 top-4 -left-1/2 z-0"
                    style={{
                      backgroundColor:
                        index <= currentStep ? "#3B82F6" : "#E5E7EB",
                    }}
                  ></div>
                )}
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-medium transition-colors duration-300 z-10 ${
                    index < currentStep
                      ? "bg-green-500"
                      : index === currentStep
                      ? "bg-blue-500"
                      : "bg-gray-300"
                  }`}
                >
                  {index < currentStep ? "✓" : index + 1}
                </div>
                <span
                  className={`mt-2 text-[10px] font-medium text-center ${
                    index === currentStep ? "text-blue-500" : "text-gray-500"
                  }`}
                >
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6">
          {isAddressStep && (
            <div className="mb-6 border-b pb-6">
              <label className="text-gray-700 text-sm font-medium mb-2 block">
                Select Your Office Location on Map{" "}
                <span className="text-red-500">*</span>
              </label>
              <MapContainer
                key={mapCenter.join("-")} // Force re-mount if center changes drastically, helps with `setView` issues
                center={mapCenter}
                zoom={markerPos ? 13 : 5}
                style={{ height: "350px", width: "100%" }}
                className="border rounded-md overflow-hidden mb-3 shadow-sm"
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <DraggableMarker
                  position={markerPos}
                  onPositionChange={handleMapInteraction}
                />
              </MapContainer>
              <p className="text-xs text-gray-500 mb-3 text-center">
                Click on map or drag marker to set location, or use button
                below.
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center text-sm hover:bg-blue-600 transition-colors"
                  onClick={getCurrentLocation}
                  disabled={isSigningUp}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Get Current Location
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {steps[currentStep].fields.map((field) => {
              if (field.showWhenVerifying && !showAadhaarOtpInput) return null;
              return (
                <div key={field.id} className="mb-1">
                  <label
                    htmlFor={field.id}
                    className="text-gray-900 text-xs font-medium mb-1 block"
                  >
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex items-center">
                    {field.type === "file" ? (
                      <input
                        type="file"
                        id={field.id}
                        name={field.id}
                        accept={field.accept}
                        onChange={(e) => handleFileChange(e, field.id)}
                        className="w-full p-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 text-xs"
                        disabled={isSigningUp || field.disabled}
                      />
                    ) : field.type === "select" ? (
                      <select
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) => handleInputChange(e, field.id)}
                        className="w-full p-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        required={field.required}
                        disabled={isSigningUp || field.disabled}
                      >
                        {field.options.map((option) => (
                          <option key={option} value={option}>
                            {option === "" ? "Select..." : option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="relative w-full">
                        <input
                          type={
                            field.id === "password" && !showPassword
                              ? "password"
                              : "text"
                          }
                          id={field.id}
                          value={formData[field.id] || ""}
                          onChange={(e) => handleInputChange(e, field.id)}
                          className={`w-full p-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs ${
                            field.disabled ? "bg-gray-100" : ""
                          }`}
                          placeholder={`Enter ${field.label}`}
                          required={field.required}
                          min={field.min}
                          max={field.max}
                          maxLength={field.maxLength}
                          readOnly={field.readOnly}
                          disabled={isSigningUp || field.disabled}
                        />
                        {field.id === "password" && (
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-4 w-4" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                    {field.verifyButton && (
                      <button
                        type="button"
                        onClick={field.verifyFunction}
                        className={`ml-2 px-3 py-2 rounded-lg text-xs flex items-center justify-center min-w-[90px] ${
                          formData[field.id + "Verified"]
                            ? "bg-green-500 text-white cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        } transition-colors`}
                        disabled={
                          isSigningUp ||
                          !formData[field.id] ||
                          field.isLoading ||
                          formData[field.id + "Verified"] ||
                          field.disabled
                        }
                      >
                        {field.isLoading ? (
                          <AiOutlineLoading3Quarters className="animate-spin" />
                        ) : formData[field.id + "Verified"] ? (
                          "Verified ✓"
                        ) : (
                          "Verify"
                        )}
                      </button>
                    )}
                  </div>
                  {/* Display Mock Cards */}
                  {field.id === "aadhaarNumber" &&
                    formData.aadhaarVerified &&
                    aadhaarCardData && (
                      <MemoizedMockAadhaarCard
                        cardData={aadhaarCardData}
                        aadhaarNumber={formData.aadhaarNumber}
                      />
                    )}
                  {field.id === "gst" &&
                    formData.gstVerified &&
                    gstCardData && (
                      <MemoizedMockGstCard cardData={gstCardData} />
                    )}
                </div>
              );
            })}
            {currentStep === steps.length - 1 && (
              <>
                <div className="col-span-full mb-3 flex flex-col items-center">
                  <label className="text-gray-700 text-xs font-medium mb-1 block text-center">
                    Profile Image <span className="text-red-500">*</span>
                  </label>
                  <div className="w-24 h-24 border-2 border-gray-400 border-dashed rounded-full flex items-center justify-center overflow-hidden mb-1 relative">
                    {profileImagePreview ? (
                      <img
                        src={profileImagePreview}
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        ></path>
                      </svg>
                    )}
                    <input
                      type="file"
                      id="profileImage"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleprofileImageChange}
                      className="opacity-0 absolute inset-0 cursor-pointer"
                      disabled={isSigningUp}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Click to upload (JPG, PNG, Max 2MB)
                  </p>
                </div>
                <div className="col-span-full">
                  <div className="space-y-2 mb-4">
                    {[
                      {
                        id: "agreementConfirmed",
                        label: "I agree to the",
                        link: "/termscondition",
                        linkText: "Terms and Conditions",
                      },
                      {
                        id: "privacyPolicyConfirmed",
                        label: "I agree to the",
                        link: "/privacypolicy",
                        linkText: "Privacy Policy",
                      },
                      {
                        id: "warrantyConfirmed",
                        label:
                          "I agree to provide a 10-year warranty service, guaranteeing long-term quality and structural integrity for all construction projects that I build.",
                        link: null,
                        linkText: "",
                      },
                    ].map((agreement) => (
                      <div key={agreement.id} className="flex items-start">
                        <input
                          type="checkbox"
                          id={agreement.id}
                          checked={formData[agreement.id]}
                          onChange={() => handleCheckboxChange(agreement.id)}
                          className="mt-0.5 form-checkbox h-3 w-3 text-blue-600"
                          disabled={isSigningUp}
                        />
                        <label
                          htmlFor={agreement.id}
                          className="ml-2 text-xs text-gray-600"
                        >
                          {agreement.label}{" "}
                          {agreement.link && (
                            <a
                              href={agreement.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline font-medium"
                            >
                              {agreement.linkText}
                            </a>
                          )}
                          .<span className="text-red-500">*</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handlePreviousStep}
              className={`px-5 py-2 rounded-lg text-xs ${
                currentStep === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              } transition-colors`}
              disabled={currentStep === 0 || isSigningUp}
            >
              Back
            </button>
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="bg-blue-500 text-white px-5 py-2 rounded-lg text-xs hover:bg-blue-600 transition-colors"
                disabled={isSigningUp}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="bg-green-500 text-white px-5 py-2 rounded-lg text-xs hover:bg-green-600 flex items-center transition-colors"
                disabled={isSigningUp}
              >
                {isSigningUp ? (
                  <>
                    <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  "Submit Registration"
                )}
              </button>
            )}
          </div>
        </form>
        <p className="mt-6 text-center text-xs text-gray-800">
          Already have an account?{" "}
          <span
            onClick={() => navigate("/login?type=builder")}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Login
          </span>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
